import React, { useState, useRef } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  PhotoCamera as PhotoCameraIcon,
  School as SchoolIcon,
  Notifications as NotificationsIcon,
  Delete as DeleteIcon,
  Logout as LogoutIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { signOut } from 'firebase/auth'
import { auth, storage, db } from '../firebase/config'
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage'
import { doc, updateDoc, getDoc, deleteDoc, collection, query, where, getDocs, writeBatch, arrayRemove } from 'firebase/firestore'
import toast from 'react-hot-toast'
import SpaceTheme from '../components/SpaceTheme'

const Settings = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)

  const handleLogout = async () => {
    try {
      // Çıkış yapmadan önce hash'i temizle
      window.location.hash = ''
      // URL'yi temizle  
      window.history.replaceState(null, '', window.location.pathname)
      
      await signOut(auth)
      toast.success('Başarıyla çıkış yaptınız')
    } catch (error) {
      console.error('Logout error:', error)
      toast.error('Çıkış yapılırken hata oluştu')
    }
  }

  const handleChangeProfilePhoto = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelect = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Dosya türü kontrolü
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir resim dosyası seçin')
      return
    }

    // Dosya boyutu kontrolü (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'dan küçük olmalıdır')
      return
    }

    setUploading(true)

    try {
      // Firebase Storage'a yükle
      const timestamp = Date.now()
      const storageRef = ref(storage, `profile_images/${user.uid}/profile_${timestamp}.jpg`)
      
      await uploadBytes(storageRef, file)
      const downloadURL = await getDownloadURL(storageRef)

      // Firestore'da kullanıcı verisini güncelle
      const userRef = doc(db, 'users', user.uid)
      await updateDoc(userRef, {
        profileImageUrl: downloadURL
      })

      toast.success('Profil fotoğrafı başarıyla güncellendi')
      
      // Sayfayı yenile
      window.location.reload()
    } catch (error) {
      console.error('Error uploading profile photo:', error)
      toast.error('Profil fotoğrafı yüklenirken hata oluştu')
    } finally {
      setUploading(false)
    }
  }

  const getProfileImageUrl = () => {
    // Eğer kullanıcı Google ile giriş yaptıysa ve profil fotoğrafı varsa
    if (user?.photoURL) {
      let photoUrl = user.photoURL
      
      // Google profil resmi URL'sini daha yüksek kaliteli hale getir
      if (photoUrl.includes('s96-c')) {
        photoUrl = photoUrl.replace('s96-c', 's800-c')
      } else if (photoUrl.includes('s400-c')) {
        photoUrl = photoUrl.replace('s400-c', 's800-c')
      } else if (!photoUrl.includes('s800-c')) {
        if (photoUrl.includes('?')) {
          photoUrl += '&sz=800'
        } else {
          photoUrl += '?sz=800'
        }
      }
      
      return photoUrl
    }
    
    // Firestore'dan gelen profil fotoğrafı
    return userData?.profileImageUrl
  }

  const handleChangeLevel = () => {
    onBack({
      page: 'level-choice'
    })
  }

  const handleNotificationSettings = () => {
    toast.success('Bildirim ayarları güncellenecek')
  }

  const handleDeleteAccount = () => {
    setShowDeleteDialog(true)
  }

  const confirmDeleteAccount = async () => {
    setShowDeleteDialog(false)
    
    try {
      // Progress göster
      toast.loading('Hesap siliniyor...', { duration: Infinity })
      
      const userId = user.uid
      
      // 1. Storage'dan profil fotoğraflarını sil
      await deleteProfileImages(userId)
      
      // 2. Firestore verilerini sil
      await deleteFirestoreData(userId)
      
      // 3. Authentication hesabını sil
      await user.delete()
      
      toast.dismiss()
      toast.success('Hesabınız başarıyla silindi')
      
      // Ana sayfaya yönlendir
      window.location.href = '/'
    } catch (error) {
      toast.dismiss()
      console.error('Error deleting account:', error)
      
      if (error.code === 'auth/requires-recent-login') {
        toast.error('Hesap silmek için son zamanlarda tekrar giriş yapmanız gerekiyor')
      } else {
        toast.error('Hesap silinirken hata oluştu: ' + error.message)
      }
    }
  }

  const deleteProfileImages = async (userId) => {
    try {
      const profileImagesRef = ref(storage, `profile_images/${userId}`)
      const result = await listAll(profileImagesRef)
      
      if (result.items.length > 0) {
        const deletePromises = result.items.map(item => deleteObject(item))
        await Promise.all(deletePromises)
      }
    } catch (error) {
      console.error('Error deleting profile images:', error)
      // Profil fotoğrafları silinmese bile devam et
    }
  }

  const deleteFirestoreData = async (userId) => {
    try {
      // Kullanıcı verilerini sil
      await deleteDoc(doc(db, 'users', userId))
      
      // Kelime listesini sil
      await deleteDoc(doc(db, 'word_lists', userId))
      
      // Öğretmen ise sınıflarını sil
      if (userData?.role === 'teacher') {
        await deleteTeacherData(userId)
      }
      
      // Öğrenci ise sınıf üyeliklerini sil
      if (userData?.role === 'student') {
        await deleteStudentData(userId)
      }
    } catch (error) {
      console.error('Error deleting Firestore data:', error)
      throw error
    }
  }

  const deleteTeacherData = async (userId) => {
    try {
      // Öğretmenin sınıflarını bul ve sil
      const classesRef = collection(db, 'classes')
      const classesQuery = query(classesRef, where('teacherId', '==', userId))
      const classesSnapshot = await getDocs(classesQuery)
      
      const deletePromises = classesSnapshot.docs.map(doc => deleteDoc(doc.ref))
      await Promise.all(deletePromises)
      
      // Öğretmenin kayıtlı görevlerini sil
      const savedTasksRef = collection(db, 'teachers', userId, 'savedTasks')
      const savedTasksSnapshot = await getDocs(savedTasksRef)
      
      const deleteTaskPromises = savedTasksSnapshot.docs.map(doc => deleteDoc(doc.ref))
      await Promise.all(deleteTaskPromises)
    } catch (error) {
      console.error('Error deleting teacher data:', error)
    }
  }

  const deleteStudentData = async (userId) => {
    try {
      // Öğrencinin sınıf üyeliklerini güncelle
      if (userData?.classIds && userData.classIds.length > 0) {
        const batch = writeBatch(db)
        
        for (const classId of userData.classIds) {
          const classRef = doc(db, 'classes', classId)
          batch.update(classRef, {
            studentIds: arrayRemove(userId)
          })
        }
        
        await batch.commit()
      }
    } catch (error) {
      console.error('Error deleting student data:', error)
    }
  }

  const settingsItems = [
    {
      id: 'profile-photo',
      title: 'Profil Fotoğrafını Değiştir',
      icon: <PhotoCameraIcon />,
      action: handleChangeProfilePhoto,
      color: 'primary'
    },
    {
      id: 'level',
      title: 'Seviye Değiştir',
      icon: <SchoolIcon />,
      action: handleChangeLevel,
      color: 'primary'
    },
    {
      id: 'notifications',
      title: 'Bildirim Ayarları',
      icon: <NotificationsIcon />,
      action: handleNotificationSettings,
      color: 'primary'
    },
    {
      id: 'delete-account',
      title: 'Hesabı Sil',
      icon: <DeleteIcon />,
      action: handleDeleteAccount,
      color: 'error'
    },
    {
      id: 'logout',
      title: 'Çıkış Yap',
      icon: <LogoutIcon />,
      action: handleLogout,
      color: 'error'
    }
  ]

  return (
    <SpaceTheme>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Ayarlar
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Profile Card */}
        <Card sx={{ 
          mb: 3, 
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
        }}>
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <Avatar
                sx={{ 
                  width: 80, 
                  height: 80, 
                  mx: 'auto',
                  mb: 2,
                  border: '3px solid #e0e0e0'
                }}
                src={getProfileImageUrl()}
              >
                {(userData?.username || user?.displayName || 'U').charAt(0).toUpperCase()}
              </Avatar>
              {uploading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px'
                  }}
                />
              )}
            </Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {userData?.username || user?.displayName || 'Kullanıcı'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
            {userData?.signInMethod === 'google' && (
              <Chip 
                label="Google Hesabı" 
                size="small" 
                color="primary" 
                sx={{ mt: 1 }}
              />
            )}
          </CardContent>
        </Card>

        {/* Settings List */}
        <Card sx={{ 
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
        }}>
          <List>
            {settingsItems.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem 
                  button 
                  onClick={item.action}
                  disabled={uploading}
                  sx={{
                    '&:hover': {
                      backgroundColor: `${item.color}.50`
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: `${item.color}.main` }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.title}
                    primaryTypographyProps={{
                      color: item.color === 'error' ? 'error.main' : 'text.primary',
                      fontWeight: 'medium'
                    }}
                  />
                </ListItem>
                {index < settingsItems.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Card>


      </Container>

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileSelect}
      />

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Hesabı Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Hesabınızı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve tüm verileriniz kalıcı olarak silinecektir.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>
            İptal
          </Button>
          <Button onClick={confirmDeleteAccount} color="error" variant="contained">
            Hesabı Sil
          </Button>
        </DialogActions>
      </Dialog>
    </SpaceTheme>
  )
}

export default Settings
